import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarIcon, Clock, RotateCcw, User, Briefcase, Bell } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format, parseISO } from 'date-fns';
import { Switch } from '@/components/ui/switch';
import type { ExtendedTask } from '@/hooks/useTasks';
import type { TaskPriority, TaskRecurrence, TaskFormData } from '@/types/tasks';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskData: TaskFormData) => void;
  task?: ExtendedTask | null;
  initialDate?: Date;
  contacts?: { id: string; name: string }[];
  deals?: { id: string; name: string }[];
  assignees?: { value: string; label: string }[];
}

const priorityOptions: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'text-muted-foreground' },
  { value: 'medium', label: 'Medium', color: 'text-blue-600' },
  { value: 'high', label: 'High', color: 'text-orange-600' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
];

const recurrenceOptions: { value: TaskRecurrence['type']; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const reminderOptions = [
  { value: 0, label: 'None' },
  { value: 5, label: '5 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 1440, label: '1 day before' },
];

const defaultContacts = [
  { id: '1', name: 'Sarah Johnson' },
  { id: '2', name: 'Michael Chen' },
  { id: '3', name: 'Emily Davis' },
  { id: '4', name: 'James Wilson' },
  { id: '5', name: 'Amanda Lee' },
];

const defaultDeals = [
  { id: 'deal_1', name: 'Enterprise Package' },
  { id: 'deal_2', name: 'Consulting Services' },
  { id: 'deal_3', name: 'Software License' },
];

export function AddTaskModal({
  isOpen,
  onClose,
  onSave,
  task,
  initialDate,
  contacts = defaultContacts,
  deals = defaultDeals,
  assignees = [
    { value: 'user1', label: 'Alex Johnson' },
    { value: 'user2', label: 'Jane Smith' },
    { value: 'user3', label: 'Mike Brown' },
  ],
}: AddTaskModalProps) {
  const isEditing = !!task;

  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: undefined,
    assignedTo: undefined,
    linkedContactId: undefined,
    linkedDealId: undefined,
    recurrence: undefined,
    reminderMinutes: 30,
  });

  const [enableRecurrence, setEnableRecurrence] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (task) {
        setFormData({
          title: task.title,
          description: task.description || '',
          priority: task.priority,
          dueDate: task.dueDate,
          assignedTo: task.assignedTo,
          linkedContactId: task.linkedContactId,
          linkedDealId: task.linkedDealId,
          recurrence: task.recurrence,
          reminderMinutes: task.reminderMinutes || 30,
        });
        setEnableRecurrence(!!task.recurrence);
      } else {
        setFormData({
          title: '',
          description: '',
          priority: 'medium',
          dueDate: initialDate?.toISOString(),
          assignedTo: undefined,
          linkedContactId: undefined,
          linkedDealId: undefined,
          recurrence: undefined,
          reminderMinutes: 30,
        });
        setEnableRecurrence(false);
      }
    }
  }, [isOpen, task, initialDate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    onSave({
      ...formData,
      recurrence: enableRecurrence ? formData.recurrence : undefined,
    });
    onClose();
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      // Preserve time if exists
      const currentDue = formData.dueDate ? new Date(formData.dueDate) : new Date();
      date.setHours(currentDue.getHours(), currentDue.getMinutes());
      setFormData((prev) => ({ ...prev, dueDate: date.toISOString() }));
    }
    setDateOpen(false);
  };

  const handleTimeChange = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = formData.dueDate ? new Date(formData.dueDate) : new Date();
    date.setHours(hours, minutes);
    setFormData((prev) => ({ ...prev, dueDate: date.toISOString() }));
  };

  const getTimeValue = () => {
    if (!formData.dueDate) return '09:00';
    const date = new Date(formData.dueDate);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Task' : 'Add New Task'}</DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Update the task details below.' 
              : 'Create a new task to keep track of your work.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              placeholder="Enter task title..."
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Add task description..."
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>

          {/* Priority & Due Date Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Priority */}
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, priority: v as TaskPriority }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className={cn('inline-flex items-center gap-2', opt.color)}>
                        <span className={cn('w-2 h-2 rounded-full', opt.color.replace('text-', 'bg-'))} />
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label>Due Date & Time</Label>
              <div className="flex gap-2">
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'flex-1 justify-start text-left font-normal',
                        !formData.dueDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.dueDate
                        ? format(new Date(formData.dueDate), 'PPP')
                        : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.dueDate ? new Date(formData.dueDate) : undefined}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                
                {formData.dueDate && (
                  <div className="flex items-center border rounded-md px-3">
                    <Clock className="h-4 w-4 text-muted-foreground mr-2" />
                    <input
                      type="time"
                      value={getTimeValue()}
                      onChange={(e) => handleTimeChange(e.target.value)}
                      className="bg-transparent border-none p-0 focus:outline-none text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Assignee */}
          <div className="space-y-2">
            <Label>Assign To</Label>
            <Select
              value={formData.assignedTo || 'unassigned'}
              onValueChange={(v) => 
                setFormData((prev) => ({ ...prev, assignedTo: v === 'unassigned' ? undefined : v }))
              }
            >
              <SelectTrigger>
                <User className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {assignees.map((assignee) => (
                  <SelectItem key={assignee.value} value={assignee.value}>
                    {assignee.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Linked Contact & Deal */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Linked Contact</Label>
              <Select
                value={formData.linkedContactId || 'none'}
                onValueChange={(v) => 
                  setFormData((prev) => ({ ...prev, linkedContactId: v === 'none' ? undefined : v }))
                }
              >
                <SelectTrigger>
                  <User className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {contacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Linked Deal</Label>
              <Select
                value={formData.linkedDealId || 'none'}
                onValueChange={(v) => 
                  setFormData((prev) => ({ ...prev, linkedDealId: v === 'none' ? undefined : v }))
                }
              >
                <SelectTrigger>
                  <Briefcase className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Select deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {deals.map((deal) => (
                    <SelectItem key={deal.id} value={deal.id}>
                      {deal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reminder */}
          <div className="space-y-2">
            <Label>Reminder</Label>
            <Select
              value={String(formData.reminderMinutes || 0)}
              onValueChange={(v) => 
                setFormData((prev) => ({ ...prev, reminderMinutes: Number(v) }))
              }
            >
              <SelectTrigger>
                <Bell className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reminderOptions.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Recurrence Toggle */}
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-muted-foreground" />
              <div>
                <Label htmlFor="recurrence" className="cursor-pointer">Recurring Task</Label>
                <p className="text-xs text-muted-foreground">Repeat this task automatically</p>
              </div>
            </div>
            <Switch
              id="recurrence"
              checked={enableRecurrence}
              onCheckedChange={setEnableRecurrence}
            />
          </div>

          {/* Recurrence Options */}
          <AnimatePresence>
            {enableRecurrence && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4 p-4 border rounded-lg bg-muted/30"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={formData.recurrence?.type || 'weekly'}
                      onValueChange={(v) => 
                        setFormData((prev) => ({
                          ...prev,
                          recurrence: { ...prev.recurrence, type: v as TaskRecurrence['type'], interval: 1 },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {recurrenceOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Every</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={formData.recurrence?.interval || 1}
                        onChange={(e) => 
                          setFormData((prev) => ({
                            ...prev,
                            recurrence: { ...prev.recurrence, interval: parseInt(e.target.value) || 1 },
                          }))
                        }
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">
                        {formData.recurrence?.type === 'daily' && 'day(s)'}
                        {formData.recurrence?.type === 'weekly' && 'week(s)'}
                        {formData.recurrence?.type === 'monthly' && 'month(s)'}
                        {formData.recurrence?.type === 'yearly' && 'year(s)'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#2B7FFF] hover:bg-[#1A5FCC]"
              disabled={!formData.title.trim()}
            >
              {isEditing ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddTaskModal;

